import {
  Badge,
  Button,
  Checkbox,
  Chip,
  Collapse,
  createStyles,
  Divider,
  fade,
  FormControlLabel,
  Grid,
  IconButton,
  makeStyles,
  Menu,
  Paper,
  Step,
  StepLabel,
  Stepper,
  SvgIcon,
  SvgIconProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Theme,
  Tooltip,
  Typography
} from '@material-ui/core';
import {
  AddRounded,
  CheckBoxRounded,
  CloseRounded,
  DeleteRounded,
  EditRounded,
  KeyboardArrowDownRounded,
  KeyboardArrowLeftRounded,
  KeyboardArrowRightRounded,
  MoreVertRounded,
  UndoRounded
} from '@material-ui/icons';
import { Alert, Autocomplete } from '@material-ui/lab';
import { KeyboardDatePicker } from '../../../components/CustomDatePicker';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import clsx from 'clsx';
import moment from 'moment';
import React, { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Redirect, useHistory } from 'react-router-dom';
import { PurposeLabel, RevisionLabel } from '.';
import { BulkProjectDocument } from '../../../api/project-documents';
import { QueryStaticEquipmentAccessory } from '../../../api/static-equipment-lists';
import { RootState } from '../../../app/store';
import { ProductNameLabel } from '../../../common/enum/ProductNameLabel';
import {
  BulkDocument,
  BulkDocumentRevision,
  DocumentRevision,
  HistoryDeleteDocumentRevision
} from '../../../common/interface/bulkPlanning';
import ConfirmDeleteBox from '../../../components/ConfirmDeleteBox';
import HelpTooltip from '../../../components/HelpTooltip';
import { DAYS_FOR_COMPLETE_REVIEW } from '../../../constants';
import { addWeekdays } from '../../../functions/addWeekdays';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import { useQueryStringNumber } from '../../../hooks/useQueryString';
import AutoAwesomeRounded from '../../../icon/AutoAwesomeRounded';
import Breadcrumb from '../../Breadcrumb';
import loadingSlice from '../../Loading/loadingSlice';
import {
  getStaticEquipmentListsAsync,
  queryStaticEquipmentAccessoryWithDocumentsAsync
} from '../../MasterData/StaticEquipmentList/staticEquipmentListSlice';
import { getProjectAsync } from '../../Project/projectSlice';
import { User } from '../../UserProfile/userProfileSlice';
import { checkProductType } from '../DocumentCreate';
import { bulkCreateProjectDocumentsAsync } from '../projectDocumentSlice';

interface StaticDocument extends Omit<BulkDocument, 'refId'> {
  ref: string;
}

interface BulkStaticDocument {
  id: number;
  name: string;
  documents: StaticDocument[];
}

interface HistoryDeleteStaticDocumentRevision
  extends Omit<HistoryDeleteDocumentRevision, 'refDocumentId'> {
  refStaticId: number;
  refDocument: string;
}

const stepKeys = [
  'bulkPlanning.mse.steps.selectEquipmentTypeAccessories',
  'bulkPlanning.steps.customizeTemplate',
  'bulkPlanning.steps.createDocuments'
];

export default function MseBulkPlanning() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const projectId = useQueryStringNumber({ query: 'projectId' });

  const { selectedProject } = useSelector((state: RootState) => state.projects);
  const { data: template, templateWithDocument } = useSelector(
    (state: RootState) => state.staticEquipmentLists
  );
  const { data: purposes } = useSelector((state: RootState) => state.purposes);
  const { data: revisions } = useSelector(
    (state: RootState) => state.revisions
  );
  const { data: accessControls } = useSelector(
    (state: RootState) => state.accessControls
  );
  const { data: documents } = useSelector(
    (state: RootState) => state.documents
  );
  const { users } = useSelector((state: RootState) => state.projectRegisters);
  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.userProfile
  );

  const [selectedStaticLists, setSelectedStaticLists] = useState<
    {
      staticEquipmentListId: number;
      quantity: number | null;
      staticAccessoryLists: {
        staticAccessoryListId: number;
        quantity: number | null;
      }[];
    }[]
  >([]);
  const [selectedDocumentRefs, setSelectedDocumentRefs] = useState<string[]>(
    []
  ); // Store  `ref` of document
  const [selectedDocument, setSelectedDocument] =
    useState<StaticDocument | null>(null);
  const [code, setCode] = useState<{
    plant: string | null;
    area: string | null;
  }>({
    plant: null,
    area: null
  });
  const defaultDocumentRevisions: DocumentRevision[] = [
    { purposeId: PurposeLabel.IFR, revisionId: RevisionLabel.A },
    { purposeId: PurposeLabel.IFC, revisionId: RevisionLabel.Zero },
    { purposeId: PurposeLabel.IFF, revisionId: RevisionLabel.One }
  ];
  const [documentRevisions, setDocumentRevisions] = useState<
    DocumentRevision[]
  >(defaultDocumentRevisions);
  const [bulkStaticDocuments, setBulkStaticDocuments] = useState<
    BulkStaticDocument[]
  >([]);
  const bulkDocuments = bulkStaticDocuments.flatMap((list) => list.documents);
  const [historyDeleteDocumentRevisions, setHistoryDeleteDocumentRevisions] =
    useState<HistoryDeleteStaticDocumentRevision[]>([]);

  const [activeStep, setActiveStep] = useState(0);
  const [openStaticEquipmentId, setOpenStaticEquipmentId] = useState<number>(0);
  const [openedDocumentRefs, setOpenedDocumentRefs] = useState<string[]>([]);
  const [isEditRevision, setIsEditRevision] = useState<boolean>(false);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const [isShowAllUsers, setIsShowAllUsers] = useState<boolean>(false);
  const [openDeleteOneDialog, setOpenDeleteOneDialog] =
    useState<boolean>(false);
  const [openDeleteAllDialog, setOpenDeleteAllDialog] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const originalNo = 'XXX-XXX-MSE-SPC-XXXX-XX';

  useEffect(() => {
    // Prepare the document in the `Create Documents` step only
    if (selectedProject && activeStep === 2) {
      const staticProjectDocument = templateWithDocument.map(
        (staticDocument, index) => {
          return {
            ...staticDocument,
            documents: staticDocument.documents.map((document, subIndex) => {
              let userIds: number[] = [];
              document.projectPositionWorkflowSteps.forEach(
                (projectPositionWorkflowStep) => {
                  if (projectPositionWorkflowStep.projectPosition) {
                    projectPositionWorkflowStep.projectPosition.userProjectPositions.forEach(
                      (userProjectPosition) =>
                        userIds.push(userProjectPosition.userId)
                    );
                  }
                }
              );
              // Filter array to have unique values
              userIds = userIds.filter((item, pos) => {
                return userIds.indexOf(item) === pos;
              });

              let documentCreatorId = null;
              // Select document creator from logged in user. If logged in user isn't document creator list, the system will select the first user
              if (userIds.length > 0) {
                documentCreatorId =
                  userIds.find((userId) => userId === loggedInUser?.id) ??
                  userIds[0];
              }

              const projectDocument: StaticDocument = {
                ref: `${index + 1}.${subIndex + 1}`, // To prevent remove wrong record when deleting document after selecting
                name: document.name,
                no: document.no,
                accessControlId: 2,
                documentCreatorId,
                disciplineId: document.disciplineId,
                documentId: document.id,
                isAIMS: false,
                projectId: selectedProject.id,
                projectDocumentRevisions: documentRevisions.map(
                  ({ purposeId, revisionId }, index) => {
                    return {
                      refId: index, // To record order of revision using for undo action
                      planIssueDate: null,
                      planCompletedDate: null,
                      manHour: 1,
                      purposeId,
                      revisionId,
                      isAutoApproval: purposeId === PurposeLabel.IFR
                    };
                  }
                ),
                userIds
              };

              // Magic for selecting REPCO as:
              const repcoId = 7;
              switch (checkProductType(selectedProject.productName)) {
                // 1. Consultant on PMC project
                case ProductNameLabel.pmc:
                  projectDocument.consultantId = repcoId;
                  break;
                // 2. Main contractor on EPC project
                case ProductNameLabel.epc:
                  projectDocument.contractorId = repcoId;
                  break;
                default:
                  break;
              }

              return projectDocument;
            })
          };
        }
      );
      setBulkStaticDocuments(staticProjectDocument);
      setOpenStaticEquipmentId(templateWithDocument[0]?.id ?? 0);
      setOpenedDocumentRefs(['1.1']);
    }
  }, [templateWithDocument, activeStep]);

  useEffect(() => {
    if (template.length === 0) {
      dispatch(getStaticEquipmentListsAsync());
    }
    if (projectId) {
      dispatch(getProjectAsync(projectId));
    }
  }, []);

  function modifiedNo(originalNo: string): string {
    let arrayFromOriginalNo = originalNo.split('-');
    // Replace plant name (at Position 0, Remove 1 item, and add `plant`)
    if (code.plant) arrayFromOriginalNo.splice(0, 1, code.plant.toUpperCase());
    // Replace area number (at Position 1, Remove 1 item, and add `area`)
    if (code.area) arrayFromOriginalNo.splice(1, 1, code.area.toUpperCase());
    // @See more: https://www.w3schools.com/jsref/jsref_splice.asp
    return arrayFromOriginalNo.join('-');
  }

  function generateRevisionChip(
    { purposeId, revisionId }: DocumentRevision,
    key: number
  ): ReactNode {
    const purpose = purposes.find((purpose) => purpose.id === purposeId);
    const revision = revisions.find((revision) => revision.id === revisionId);

    return (
      <React.Fragment key={key}>
        <Tooltip
          title={t('bulkPlanning.mse.purposeRevisionTooltip', { purposeFullName: purpose?.fullName, revisionName: revision?.name }) as string}
          arrow
        >
          <Chip
            className={classes.truncatedChip}
            variant="outlined"
            label={t('bulkPlanning.mse.purposeRevisionLabel', { purposeAbbr: purpose?.abbreviation, revisionName: revision?.name })}
          />
        </Tooltip>
      </React.Fragment>
    );
  }

  function nextButton(step: number): { disabled: boolean; action: any } {
    switch (step) {
      case 0:
        return {
          disabled:
            selectedStaticLists.filter(
              (list) => list.quantity && list.quantity > 0
            ).length === 0,
          action: handleNext
        };
      case 1:
        return {
          disabled: isEditRevision || loading,
          action: async () => {
            try {
              setLoading(true);
              if (projectId) {
                const query: QueryStaticEquipmentAccessory = {
                  projectId,
                  plant: code.plant,
                  area: code.area,
                  staticEquipmentLists: selectedStaticLists
                    .filter(
                      (list) =>
                        list.quantity &&
                        list.quantity != null &&
                        list.quantity > 0
                    )
                    .map((list) => {
                      return {
                        ...list,
                        staticAccessoryLists: list.staticAccessoryLists.filter(
                          (list) =>
                            list.quantity &&
                            list.quantity != null &&
                            list.quantity > 0
                        )
                      };
                    })
                };
                await dispatch(
                  queryStaticEquipmentAccessoryWithDocumentsAsync(query)
                ).unwrap();
                setOpenedDocumentRefs([]);
                setSelectedDocumentRefs([]);
                setHistoryDeleteDocumentRevisions([]);
                setTimeout(() => {
                  handleNext();
                  setLoading(false);
                }, 250);
              }
            } catch {
              setLoading(false);
            }
          }
        };
      case 2:
        return {
          disabled:
            bulkDocuments.length === 0 ||
            bulkDocuments.some(
              (document) =>
                !document.name ||
                !document.no ||
                !document.accessControlId ||
                !document.documentCreatorId ||
                document.projectDocumentRevisions.some(
                  (documentRevision) =>
                    !documentRevision.purposeId ||
                    !documentRevision.revisionId ||
                    !documentRevision.manHour ||
                    !documentRevision.planIssueDate ||
                    !documentRevision.planCompletedDate
                )
            ) ||
            loading,
          action: handleNext // The action in this step doesn't use, we will use `onSubmit` function instead
        };
      default:
        return { disabled: true, action: handleNext };
    }
  }

  function handleNext(): void {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  }

  function handleBack(): void {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  }

  function handleEditRevision(): void {
    setIsEditRevision((prevEdit) => !prevEdit);
  }

  function handleChangeEquipment(
    staticEquipmentListId: number,
    newValue: string
  ) {
    // Handle quantity in integer format
    const quantity =
      newValue === '' || Number.parseFloat(newValue) <= 0
        ? null
        : Number.parseFloat(newValue);

    const equipmentListIndex = selectedStaticLists.findIndex(
      (list) => list.staticEquipmentListId === staticEquipmentListId
    );

    if (equipmentListIndex !== -1) {
      // Update existing equipment
      const updatedEquipment = {
        ...selectedStaticLists[equipmentListIndex],
        quantity
      };
      selectedStaticLists.splice(equipmentListIndex, 1, updatedEquipment);
    } else {
      // Add new equipment
      selectedStaticLists.push({
        staticEquipmentListId,
        quantity,
        staticAccessoryLists: []
      });
    }
    setSelectedStaticLists([...selectedStaticLists]);
  }

  function handleChangeAccessory(
    staticEquipmentListId: number,
    staticAccessoryListId: number,
    newValue: string
  ) {
    // Handle quantity in integer format
    const quantity =
      newValue === '' || Number.parseFloat(newValue) <= 0
        ? null
        : Number.parseFloat(newValue);

    const equipmentListIndex = selectedStaticLists.findIndex(
      (list) => list.staticEquipmentListId === staticEquipmentListId
    );

    if (equipmentListIndex !== -1) {
      const accessoryListIndex = selectedStaticLists[
        equipmentListIndex
      ].staticAccessoryLists.findIndex(
        (list) => list.staticAccessoryListId === staticAccessoryListId
      );

      if (accessoryListIndex !== -1) {
        // Update existing accessory
        const updatedAccessory = {
          ...selectedStaticLists[equipmentListIndex].staticAccessoryLists[
            accessoryListIndex
          ],
          quantity
        };
        selectedStaticLists[equipmentListIndex].staticAccessoryLists.splice(
          accessoryListIndex,
          1,
          updatedAccessory
        );
      } else {
        // Add new accessory
        selectedStaticLists[equipmentListIndex].staticAccessoryLists.push({
          staticAccessoryListId,
          quantity
        });
      }
      setSelectedStaticLists([...selectedStaticLists]);
    } else {
      console.error(
        `Equipment with ID: ${staticEquipmentListId} not found. Unable to update accessory.`
      );
    }
  }

  function handleOpenStaticEquipment(id: number): void {
    const open = getStatus(id, [openStaticEquipmentId]);
    if (open) {
      setOpenStaticEquipmentId(0);
    } else {
      setOpenStaticEquipmentId(id);
    }
  }

  function handleOpenDocument(ref: string): void {
    const open = getStatus(ref, openedDocumentRefs);
    if (open) {
      // If the document is already open, remove it from the list
      setOpenedDocumentRefs(
        openedDocumentRefs.filter(
          (openedDocumentRef) => openedDocumentRef !== ref
        )
      );
    } else {
      // Add the document to the list, ensuring a maximum of 5 documents
      setOpenedDocumentRefs((prevRefs) => {
        const updatedRefs = [...prevRefs, ref];
        return updatedRefs.length > 5 ? updatedRefs.slice(1) : updatedRefs;
      });
    }
  }

  function handleSelectDocument(ref: string): void {
    const selected = getStatus(ref, selectedDocumentRefs);
    if (selected) {
      setSelectedDocumentRefs(
        selectedDocumentRefs.filter(
          (selectedDocumentRef) => selectedDocumentRef !== ref
        )
      );
    } else {
      setSelectedDocumentRefs([...selectedDocumentRefs, ref]);
    }
  }

  function handleSelectAllClick(
    event: React.ChangeEvent<HTMLInputElement>
  ): void {
    if (event.target.checked) {
      const selectedDocumentRefs = bulkDocuments.map(
        (document) => document.ref
      );
      setSelectedDocumentRefs(selectedDocumentRefs);
    } else {
      setSelectedDocumentRefs([]);
    }
  }

  function getStatus<T>(value: T, selectedItems: T[]): boolean {
    return selectedItems.indexOf(value) !== -1;
  }

  function getValue<T extends { id: number }>(
    id: number | null,
    options: T[]
  ): T | null {
    return options.find((option) => option.id === id) ?? null;
  }

  function getCountDocument(
    staticEquipmentId: number,
    type: 'selected' | 'error'
  ): number {
    const documents =
      bulkStaticDocuments.find(({ id }) => id === staticEquipmentId)
        ?.documents ?? [];

    if (type === 'selected') {
      const selectedDocuments = documents.filter((document) =>
        selectedDocumentRefs.some((ref) => ref === document.ref)
      );
      return selectedDocuments.length;
    }

    if (type === 'error') {
      const errorDocuments = documents.filter(
        (document) =>
          !document.name ||
          !document.no ||
          !document.accessControlId ||
          !document.documentCreatorId ||
          document.projectDocumentRevisions.some(
            (documentRevision) =>
              !documentRevision.purposeId ||
              !documentRevision.revisionId ||
              !documentRevision.manHour ||
              !documentRevision.planIssueDate ||
              !documentRevision.planCompletedDate
          )
      );
      return errorDocuments.length;
    }

    return 0;
  }

  function handleChangeTextField(
    event: React.ChangeEvent<HTMLInputElement>
  ): void {
    const { name, value } = event.target;
    setCode({
      ...code,
      [name]: value === '' ? null : value.toUpperCase().substring(0, 3)
    });
  }

  function handleChangeRevisionTemplate(
    value: number | undefined,
    field: 'purposeId' | 'revisionId',
    index: number
  ): void {
    const documentRevision = documentRevisions[index];

    // Create updated revision object
    const updatedDocumentRevision = {
      ...documentRevision,
      [field]: value ?? null
    };

    // {t('bulkPlanning.actions.reset')} `revisionId` if `purposeId` is changed
    if (field === 'purposeId') {
      updatedDocumentRevision.revisionId = null;
    }

    // Update the specific revision in place
    documentRevisions.splice(index, 1, updatedDocumentRevision);
    setDocumentRevisions([...documentRevisions]);
  }

  function handleRemoveRevisionTemplate(index: number): void {
    // Remove the revision at the specified index
    documentRevisions.splice(index, 1);
    setDocumentRevisions([...documentRevisions]);
  }

  function handleNewRevisionTemplate(): void {
    setDocumentRevisions([
      ...documentRevisions,
      { purposeId: null, revisionId: null }
    ]);
  }

  function handleResetRevisionTemplate(): void {
    setDocumentRevisions(defaultDocumentRevisions);
  }

  function handleAnchorElement(
    open: boolean
  ): (event: React.MouseEvent<HTMLButtonElement>) => void {
    return function (event: React.MouseEvent<HTMLButtonElement>) {
      setAnchorElement(open ? event.currentTarget : null);
    };
  }

  function getRelatedUser(userIds: number[], users: User[]): User[] {
    return !isShowAllUsers
      ? users.filter((user) => userIds.some((userId) => userId === user.id))
      : users;
  }

  function handleChangeRelatedUsers(): void {
    setIsShowAllUsers((prev) => !prev);
  }

  function handleChangeDocumentDetail(
    value: string | number,
    field: 'name' | 'no' | 'documentCreatorId',
    staticEquipmentIndex: number,
    documentIndex: number
  ): void {
    const staticDocument = bulkStaticDocuments[staticEquipmentIndex];
    const document = staticDocument.documents[documentIndex];

    const newDocument = { ...document, [field]: value === '' ? null : value };
    staticDocument.documents.splice(documentIndex, 1, newDocument);
    bulkStaticDocuments.splice(staticEquipmentIndex, 1, {
      ...staticDocument,
      documents: staticDocument.documents
    });
    setBulkStaticDocuments([...bulkStaticDocuments]);
  }

  function handleChangeDocumentRevisionDetail(
    value: string | number | boolean | MaterialUiPickersDate,
    field:
      | 'purposeId'
      | 'revisionId'
      | 'isAutoApproval'
      | 'manHour'
      | 'planIssueDate'
      | 'planCompletedDate',
    staticEquipmentIndex: number,
    documentIndex: number,
    documentRevisionIndex: number,
    options?: { positiveNumber: boolean }
  ): void {
    const staticDocument = bulkStaticDocuments[staticEquipmentIndex];
    const document = staticDocument.documents[documentIndex];
    const documentRevision =
      document.projectDocumentRevisions[documentRevisionIndex];

    const newDocumentRevision = {
      ...documentRevision,
      [field]:
        options?.positiveNumber && typeof value === 'string'
          ? Number.parseFloat(value) <= 0
            ? null
            : Number.parseFloat(value)
          : value === ''
          ? null
          : value
    };
    if (field === 'purposeId') {
      newDocumentRevision.revisionId = null;
      newDocumentRevision.isAutoApproval = value === PurposeLabel.IFR;
    }
    if (field === 'planIssueDate') {
      if (value instanceof Date) {
        newDocumentRevision.planCompletedDate = addWeekdays(
          value,
          DAYS_FOR_COMPLETE_REVIEW
        );
      } else {
        newDocumentRevision.planCompletedDate = null;
      }
    }

    document.projectDocumentRevisions.splice(
      documentRevisionIndex,
      1,
      newDocumentRevision
    );
    staticDocument.documents.splice(documentIndex, 1, {
      ...document,
      projectDocumentRevisions: document.projectDocumentRevisions
    });
    bulkStaticDocuments.splice(staticEquipmentIndex, 1, {
      ...staticDocument,
      documents: staticDocument.documents
    });
    setBulkStaticDocuments([...bulkStaticDocuments]);
  }

  function handleOpenDeleteOneDialog(document?: StaticDocument): void {
    setOpenDeleteOneDialog((prev) => !prev);
    if (document) setSelectedDocument(document);
  }

  function handleOpenDeleteAllDialog(): void {
    setOpenDeleteAllDialog((prev) => !prev);
  }

  async function onReset(): Promise<void> {
    if (projectId) {
      const query: QueryStaticEquipmentAccessory = {
        projectId,
        plant: code.plant,
        area: code.area,
        staticEquipmentLists: selectedStaticLists
          .filter((list) => list.quantity && list.quantity > 0)
          .map((list) => {
            return {
              ...list,
              staticAccessoryLists: list.staticAccessoryLists.filter(
                (list) => list.quantity && list.quantity > 0
              )
            };
          })
      };
      await dispatch(
        queryStaticEquipmentAccessoryWithDocumentsAsync(query)
      ).unwrap();
    }
  }

  function onDeleteDocument(): void {
    if (selectedDocument) {
      const newStaticDocument = bulkStaticDocuments.map((staticDocument) => {
        return {
          ...staticDocument,
          documents: staticDocument.documents.filter(
            (document) => document.ref !== selectedDocument.ref
          )
        };
      });
      const newOpenedDocumentRefs = openedDocumentRefs.filter(
        (ref) => selectedDocument.ref !== ref
      );
      const newHistoryDeleteDocumentRevisions =
        historyDeleteDocumentRevisions.filter(
          ({ refDocument }) => selectedDocument.ref !== refDocument
        );
      const newSelectedDocumentRefs = selectedDocumentRefs.filter(
        (ref) => selectedDocument.ref !== ref
      );
      setBulkStaticDocuments(newStaticDocument);
      setOpenedDocumentRefs(newOpenedDocumentRefs);
      setHistoryDeleteDocumentRevisions(newHistoryDeleteDocumentRevisions);
      setSelectedDocumentRefs(newSelectedDocumentRefs);
      setOpenDeleteOneDialog(false);
    }
  }

  function onDeleteSelectedDocuments(): void {
    const newStaticDocument = bulkStaticDocuments.map((staticDocument) => {
      return {
        ...staticDocument,
        documents: staticDocument.documents.filter((document) =>
          selectedDocumentRefs.every((ref) => ref !== document.ref)
        )
      };
    });
    const newOpenedDocumentRefs = openedDocumentRefs.filter((id) =>
      selectedDocumentRefs.every((ref) => ref !== id)
    );
    const newHistoryDeleteDocumentRevisions =
      historyDeleteDocumentRevisions.filter(({ refDocument }) =>
        selectedDocumentRefs.every((ref) => ref !== refDocument)
      );
    setBulkStaticDocuments(newStaticDocument);
    setOpenedDocumentRefs(newOpenedDocumentRefs);
    setHistoryDeleteDocumentRevisions(newHistoryDeleteDocumentRevisions);
    setSelectedDocumentRefs([]);
    setOpenDeleteAllDialog(false);
  }

  function onDeleteDocumentRevision(
    staticEquipmentId: number,
    document: StaticDocument,
    documentRevision: BulkDocumentRevision
  ): void {
    const staticEquipmentIndex = bulkStaticDocuments.findIndex(
      ({ id }) => id === staticEquipmentId
    );
    if (staticEquipmentIndex >= 0) {
      const documentIndex = bulkStaticDocuments[
        staticEquipmentIndex
      ].documents.findIndex(({ ref }) => ref === document.ref);
      if (documentIndex >= 0) {
        // Collect deleted revision to history called `historyDeleteDocumentRevisions`
        const historyDocumentRevision: HistoryDeleteStaticDocumentRevision = {
          refStaticId: staticEquipmentId,
          refDocument: document.ref,
          projectDocumentRevision: documentRevision
        };
        setHistoryDeleteDocumentRevisions([
          historyDocumentRevision,
          ...historyDeleteDocumentRevisions
        ]);
        // Remove the revision from selected projectDocumentRevisions
        const newDocumentRevisions = document.projectDocumentRevisions.filter(
          ({ refId }) => refId !== documentRevision.refId
        );

        bulkStaticDocuments[staticEquipmentIndex].documents[documentIndex] = {
          ...document,
          projectDocumentRevisions: newDocumentRevisions
        };
        setBulkStaticDocuments([...bulkStaticDocuments]);
      }
    }
  }

  function onUndoDocumentRevision(document?: StaticDocument): void {
    if (document) {
      const history = historyDeleteDocumentRevisions.find(
        ({ refDocument }) => refDocument === document.ref
      );

      if (history) {
        const { refStaticId, projectDocumentRevision } = history;
        const staticEquipmentIndex = bulkStaticDocuments.findIndex(
          ({ id }) => id === refStaticId
        );
        if (staticEquipmentIndex >= 0) {
          const documentIndex = bulkStaticDocuments[
            staticEquipmentIndex
          ].documents.findIndex(({ ref }) => ref === document.ref);

          if (documentIndex >= 0) {
            // Restore the latest deleted document revision
            const newDocumentRevisions = [
              ...document.projectDocumentRevisions,
              projectDocumentRevision
            ].sort((a, b) => a.refId - b.refId);

            bulkStaticDocuments[staticEquipmentIndex].documents[documentIndex] =
              {
                ...document,
                projectDocumentRevisions: newDocumentRevisions
              };
            setBulkStaticDocuments([...bulkStaticDocuments]);

            // Remove from history
            setHistoryDeleteDocumentRevisions(
              historyDeleteDocumentRevisions.filter(
                (history) =>
                  !(
                    history.refDocument === document.ref &&
                    history.projectDocumentRevision.refId ===
                      projectDocumentRevision.refId
                  )
              )
            );
          }
        }
      }
    } else {
      const latestDeleteDocumentRevision = historyDeleteDocumentRevisions[0];
      if (latestDeleteDocumentRevision) {
        const { refStaticId, refDocument } = latestDeleteDocumentRevision;
        const staticEquipmentIndex = bulkStaticDocuments.findIndex(
          ({ id }) => id === refStaticId
        );

        if (staticEquipmentIndex >= 0) {
          const document = bulkStaticDocuments[
            staticEquipmentIndex
          ].documents.find(({ ref }) => ref === refDocument);

          if (document) {
            onUndoDocumentRevision(document);
          }
        }
      }
    }
  }

  async function onSubmit(
    event: React.FormEvent<HTMLFormElement>
  ): Promise<void> {
    event.preventDefault();

    // Add condition to check the value of bulkDocuments
    const isValidBulkDocuments = bulkDocuments.every((document) => {
      return (
        document.name &&
        document.no &&
        document.accessControlId &&
        document.documentCreatorId &&
        document.disciplineId &&
        document.documentId &&
        document.projectId &&
        document.projectDocumentRevisions.every((revision) => {
          return (
            revision.planIssueDate &&
            revision.planCompletedDate &&
            revision.manHour &&
            revision.purposeId &&
            revision.revisionId
          );
        })
      );
    });

    try {
      setLoading(true);
      if (isValidBulkDocuments) {
        await dispatch(
          bulkCreateProjectDocumentsAsync({
            projectDocuments: bulkDocuments as BulkProjectDocument[]
          })
        ).unwrap();
        dispatch(
          loadingSlice.actions.success({
            message: t('bulkPlanning.common.documentsCreatedSuccess')
          })
        );
        handleNext();
        setTimeout(() => {
          if (projectId) history.push(`/projects/${projectId}`);
        }, 1000);
      } else {
        throw new Error(t('bulkPlanning.common.checkAllFields') as string);
      }
    } catch (error) {
      setLoading(false);
      if (error instanceof Error)
        dispatch(
          loadingSlice.actions.error({
            message: error.message
          })
        );
    }
  }

  if (!projectId) {
    return <Redirect to="/projects" />;
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name="createBulkPlanning"
        data={{
          projectName: selectedProject?.name,
          projectId: selectedProject?.id,
          createBulkPlanning: 'MSE'
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('bulkPlanning.titles.MSE')}
      </Typography>
      <Stepper
        className={classes.card}
        component={Paper}
        activeStep={activeStep}
        alternativeLabel
      >
        {stepKeys.map((key) => (
          <Step key={key}>
            <StepLabel>{t(key)}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <form onSubmit={onSubmit}>
        <Paper className={classes.card}>
          <Collapse in={activeStep === 0}>
            <div className={classes.flexSpace}>
              <Typography variant="h6" display="inline">
                {t('bulkPlanning.mse.equipmentTypeAccessories')}
              </Typography>
            </div>
            <Divider className={classes.divider} />
            <TableContainer style={{ overflowY: 'hidden' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell align="right" width={64}>
                      {t('bulkPlanning.mse.item')}
                    </TableCell>
                    <TableCell className={classes.flexGrow}>
                      {t('bulkPlanning.mse.equipmentTypeAccessories')}
                    </TableCell>
                    <TableCell align="center" width={100}>
                      {t('bulkPlanning.mse.quantity')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {template.map((staticEquipmentList, index) => {
                    const quantity =
                      selectedStaticLists.find(
                        (list) =>
                          list.staticEquipmentListId === staticEquipmentList.id
                      )?.quantity ?? '';
                    return (
                      <React.Fragment key={index}>
                        <TableRow className={classes.unsetAll} hover>
                          <TableCell align="right">{index + 1}</TableCell>
                          <TableCell>{staticEquipmentList.name}</TableCell>
                          <TableCell className={classes.px2}>
                            <TextField
                              type="number"
                              value={quantity}
                              onChange={(event) =>
                                handleChangeEquipment(
                                  staticEquipmentList.id,
                                  event.target.value
                                )
                              }
                              fullWidth
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell padding="none" colSpan={3}>
                            <Collapse in={Number(quantity) > 0}>
                              <Table className={classes.mb1} size="small">
                                <TableBody>
                                  {staticEquipmentList.staticAccessoryLists.map(
                                    (staticAccessoryList, subIndex) => {
                                      const quantity =
                                        selectedStaticLists
                                          .find(
                                            (list) =>
                                              list.staticEquipmentListId ===
                                              staticEquipmentList.id
                                          )
                                          ?.staticAccessoryLists.find(
                                            (list) =>
                                              list.staticAccessoryListId ===
                                              staticAccessoryList.id
                                          )?.quantity ?? '';
                                      return (
                                        <React.Fragment key={subIndex}>
                                          <TableRow
                                            className={classes.unsetAll}
                                            hover
                                          >
                                            <TableCell
                                              className={classes.pr2}
                                              width={64}
                                            />
                                            <TableCell className={classes.pr2}>
                                              {staticAccessoryList.name}
                                            </TableCell>
                                            <TableCell
                                              className={classes.px2}
                                              width={100}
                                            >
                                              <TextField
                                                type="number"
                                                size="small"
                                                value={quantity}
                                                onChange={(event) =>
                                                  handleChangeAccessory(
                                                    staticEquipmentList.id,
                                                    staticAccessoryList.id,
                                                    event.target.value
                                                  )
                                                }
                                                fullWidth
                                              />
                                            </TableCell>
                                          </TableRow>
                                        </React.Fragment>
                                      );
                                    }
                                  )}
                                </TableBody>
                              </Table>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
          <Collapse in={activeStep === 1}>
            <Typography variant="h6">{t('bulkPlanning.labels.documentNo')}</Typography>
            <Divider className={classes.divider} />
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Alert severity="info" icon={<AutoAwesomeRounded />}>
                  {t('bulkPlanning.common.systemUpdateDocuments')}
                </Alert>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('bulkPlanning.plantCode')}
                  variant="outlined"
                  size="small"
                  name="plant"
                  fullWidth
                  value={code.plant ? code.plant : ''}
                  onChange={handleChangeTextField}
                  placeholder={t('bulkPlanning.common.maxCharacters') as string}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('bulkPlanning.areaCode')}
                  variant="outlined"
                  size="small"
                  name="area"
                  fullWidth
                  value={code.area ? code.area : ''}
                  onChange={handleChangeTextField}
                  placeholder={t('bulkPlanning.common.maxCharacters') as string}
                />
              </Grid>
              {(code.plant || code.area) && (
                <Grid item xs={12}>
                  <Typography className={classes.px1} variant="caption">
                    {t('bulkPlanning.common.exampleDocumentNo', { originalNo, modifiedNo: modifiedNo(originalNo) })}
                  </Typography>
                </Grid>
              )}
            </Grid>
            <Divider className={classes.my2} />
            <div className={classes.header}>
              <Typography variant="h6">{t('bulkPlanning.labels.revisions')}</Typography>
              {!isEditRevision && (
                <Tooltip title={t('bulkPlanning.tooltips.edit') as string} arrow>
                  <IconButton size="small" onClick={handleEditRevision}>
                    <EditRounded />
                  </IconButton>
                </Tooltip>
              )}
              {isEditRevision && (
                <Tooltip title={t('bulkPlanning.tooltips.newRevision') as string} arrow>
                  <IconButton size="small" onClick={handleNewRevisionTemplate}>
                    <AddRounded />
                  </IconButton>
                </Tooltip>
              )}
            </div>
            <Divider className={classes.divider} />
            <Grid container spacing={1}>
              {!isEditRevision && (
                <Grid className={classes.revisionContainer} item xs={12}>
                  {documentRevisions.map((documentRevision, index) =>
                    generateRevisionChip(documentRevision, index)
                  )}
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography className={classes.px1} variant="caption">
                  <b>{t('bulkPlanning.labels.definition')}:</b>{' '}
                  {purposes
                    .map(
                      (purpose) =>
                        `${purpose.abbreviation}: ${purpose.fullName}`
                    )
                    .join(', ')}
                </Typography>
              </Grid>
              {isEditRevision && (
                <>
                  {documentRevisions.map((documentRevision, index) => {
                    const purpose = getValue(
                      documentRevision.purposeId,
                      purposes
                    );
                    const revisionList = purpose?.revisions ?? [];
                    const revision = getValue(
                      documentRevision.revisionId,
                      revisionList
                    );

                    return (
                      <Grid
                        className={classes.flexSpace}
                        item
                        xs={12}
                        key={index}
                      >
                        <Autocomplete
                          className={classes.flexGrow2}
                          id="purpose"
                          options={purposes}
                          getOptionLabel={(option) =>
                            `${option.abbreviation}: ${option.fullName}`
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t('bulkPlanning.tableHeaders.purpose')}
                              variant="outlined"
                              size="small"
                              required
                            />
                          )}
                          value={purpose}
                          onChange={(event, newValue) =>
                            handleChangeRevisionTemplate(
                              newValue?.id,
                              'purposeId',
                              index
                            )
                          }
                        />
                        <div
                          className={clsx(classes.flexSpace, classes.flexGrow)}
                        >
                          <Autocomplete
                            className={classes.flexGrow}
                            id="revision"
                            options={revisionList}
                            getOptionLabel={(option) => option.name}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={t('common.revision')}
                                variant="outlined"
                                size="small"
                                required
                              />
                            )}
                            value={revision}
                            onChange={(event, newValue) =>
                              handleChangeRevisionTemplate(
                                newValue?.id,
                                'revisionId',
                                index
                              )
                            }
                          />
                          <div>
                            <Tooltip
                              title={
                                documentRevisions.length <= 1
                                  ? (t('bulkPlanning.common.cannotRemove') as string) : (t('bulkPlanning.common.remove') as string)
                              }
                              arrow
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={documentRevisions.length <= 1}
                                  onClick={() =>
                                    handleRemoveRevisionTemplate(index)
                                  }
                                >
                                  <CloseRounded />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </div>
                        </div>
                      </Grid>
                    );
                  })}
                  <Grid item xs={12} sm={6}>
                    <Button
                      color="primary"
                      variant="outlined"
                      fullWidth
                      onClick={handleResetRevisionTemplate}
                    >
                      {t('bulkPlanning.actions.reset')}
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      color="primary"
                      variant="contained"
                      disableElevation
                      fullWidth
                      disabled={documentRevisions.some(
                        ({ purposeId, revisionId }) => !purposeId || !revisionId
                      )}
                      onClick={handleEditRevision}
                    >
                      {t('bulkPlanning.actions.update')}
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </Collapse>
          <Collapse in={activeStep >= 2}>
            <div className={classes.flexSpace}>
              <Typography variant="h6" display="inline">
                {t('bulkPlanning.listOfDocuments')}
              </Typography>
              {bulkDocuments.length > 0 && (
                <Typography display="inline">
                  {' '}
                  ({bulkDocuments.length} {t('common.result')}
                  {bulkDocuments.length > 1 && ` ${t('common.results')}`})
                </Typography>
              )}
            </div>
            <Divider className={classes.divider} />
            {activeStep >= 2 && // To improve the performance, This part will be rendered on it's step only
            bulkStaticDocuments.length > 0 &&
            bulkDocuments.length > 0 ? (
              <TableContainer style={{ overflowY: 'hidden' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="none" width={40} />
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          indeterminate={
                            selectedDocumentRefs.length > 0 &&
                            selectedDocumentRefs.length < bulkDocuments.length
                          }
                          checked={
                            selectedDocumentRefs.length > 0 &&
                            selectedDocumentRefs.length === bulkDocuments.length
                          }
                          onChange={handleSelectAllClick}
                        />
                      </TableCell>
                      <TableCell align="right" width={64}>
                        {t('bulkPlanning.tableHeaders.no')}
                      </TableCell>
                      <TableCell>{t('bulkPlanning.tableHeaders.documentName')}</TableCell>
                      <TableCell>{t('bulkPlanning.tableHeaders.documentNo')}</TableCell>
                      <TableCell>{t('bulkPlanning.tableHeaders.documentLevel')}</TableCell>
                      <TableCell className={classes.creatorField}>
                        {t('bulkPlanning.tableHeaders.documentCreator')}
                        <div>
                          <Badge
                            color="secondary"
                            variant="dot"
                            overlap="circle"
                            invisible={
                              Boolean(anchorElement) || !isShowAllUsers
                            }
                          >
                            <IconButton
                              size="small"
                              edge="end"
                              onClick={handleAnchorElement(true)}
                            >
                              <MoreVertRounded />
                            </IconButton>
                          </Badge>
                        </div>
                        <Menu
                          anchorEl={anchorElement}
                          keepMounted
                          getContentAnchorEl={null}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right'
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right'
                          }}
                          open={Boolean(anchorElement)}
                          onClose={handleAnchorElement(false)}
                        >
                          <FormControlLabel
                            className={classes.marginFormControlLabel}
                            control={
                              <Checkbox
                                checked={isShowAllUsers}
                                onChange={handleChangeRelatedUsers}
                              />
                            }
                            label={t('bulkPlanning.showAllUsers') as string}
                          />
                        </Menu>
                      </TableCell>
                      <TableCell width={40} align="center" padding="none">
                        {selectedDocumentRefs.length > 0 && (
                          <Tooltip title={t('bulkPlanning.tooltips.delete') as string} arrow>
                            <IconButton
                              size="small"
                              onClick={handleOpenDeleteAllDialog}
                            >
                              <DeleteRounded />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bulkStaticDocuments.map(
                      (staticEquipment, staticEquipmentIndex) => {
                        const open = getStatus(staticEquipment.id, [
                          openStaticEquipmentId
                        ]);
                        const countSelected = getCountDocument(
                          staticEquipment.id,
                          'selected'
                        );
                        const countError = getCountDocument(
                          staticEquipment.id,
                          'error'
                        );
                        return (
                          <React.Fragment key={staticEquipmentIndex}>
                            <TableRow>
                              <TableCell
                                className={classes.staticEquipmentRow}
                                align="center"
                                padding="none"
                              >
                                <Badge badgeContent={countError} color="error">
                                  <IconButton
                                    size="small"
                                    color="inherit"
                                    onClick={() =>
                                      handleOpenStaticEquipment(
                                        staticEquipment.id
                                      )
                                    }
                                  >
                                    {open ? (
                                      <KeyboardArrowDownRounded />
                                    ) : (
                                      <KeyboardArrowRightRounded />
                                    )}
                                  </IconButton>
                                </Badge>
                              </TableCell>
                              <TableCell
                                className={classes.staticEquipmentRow}
                                colSpan={7}
                              >
                                {countSelected > 0 && (
                                  <Chip
                                    className={classes.countBadge}
                                    label={countSelected}
                                    size="small"
                                    color="primary"
                                    icon={<CheckBoxRounded />}
                                  />
                                )}
                                <Typography variant="h6" display="inline">
                                  {`${staticEquipment.name} (${staticEquipment.documents.length})`}
                                </Typography>
                              </TableCell>
                            </TableRow>
                            {open &&
                              (staticEquipment.documents.length > 0 ? (
                                staticEquipment.documents.map(
                                  (document, documentIndex) => {
                                    const accessControl = getValue(
                                      document.accessControlId,
                                      accessControls
                                    );
                                    const documentTemplate = getValue(
                                      document.documentId,
                                      documents
                                    );
                                    const open = getStatus(
                                      document.ref,
                                      openedDocumentRefs
                                    );
                                    const showError =
                                      !open &&
                                      document.projectDocumentRevisions.some(
                                        (documentRevision) =>
                                          !documentRevision.purposeId ||
                                          !documentRevision.revisionId ||
                                          !documentRevision.manHour ||
                                          !documentRevision.planIssueDate ||
                                          !documentRevision.planCompletedDate
                                      );
                                    const selectedRow = getStatus(
                                      document.ref,
                                      selectedDocumentRefs
                                    );
                                    const selected = showError || selectedRow;
                                    const canUndo =
                                      selectedDocumentRefs.length === 0 &&
                                      historyDeleteDocumentRevisions.filter(
                                        ({ refDocument }) =>
                                          refDocument === document.ref
                                      ).length > 0;

                                    return (
                                      <React.Fragment key={documentIndex}>
                                        <TableRow
                                          classes={{
                                            root: clsx(classes.unsetAll, {
                                              [selectedRow
                                                ? classes.tableRowRoot
                                                : classes.tableRowRootError]:
                                                selected
                                            }),
                                            selected: clsx({
                                              [selectedRow
                                                ? classes.tableRowSelected
                                                : classes.tableRowSelectedError]:
                                                selected
                                            })
                                          }}
                                          selected={selected}
                                          hover
                                        >
                                          <TableCell
                                            align="center"
                                            padding="none"
                                          >
                                            <IconButton
                                              size="small"
                                              onClick={() =>
                                                handleOpenDocument(document.ref)
                                              }
                                            >
                                              {open ? (
                                                <KeyboardArrowDownRounded />
                                              ) : (
                                                <KeyboardArrowRightRounded />
                                              )}
                                            </IconButton>
                                          </TableCell>
                                          <TableCell
                                            align="center"
                                            padding="checkbox"
                                          >
                                            <Checkbox
                                              color="primary"
                                              checked={selectedRow}
                                              onClick={() =>
                                                handleSelectDocument(
                                                  document.ref
                                                )
                                              }
                                            />
                                          </TableCell>
                                          <TableCell align="right">
                                            {documentIndex + 1}
                                          </TableCell>
                                          <TableCell>
                                            <TextField
                                              className={clsx({
                                                [classes.errorInputUnderline]:
                                                  !document.name
                                              })}
                                              id="document-name"
                                              name="name"
                                              multiline
                                              fullWidth
                                              helperText={
                                                documentTemplate &&
                                                documentTemplate.name !==
                                                  document.name &&
                                                t('bulkPlanning.mse.fromTemplate', { templateName: documentTemplate.name ?? '' })
                                              }
                                              value={document.name}
                                              onChange={(event) =>
                                                handleChangeDocumentDetail(
                                                  event.target.value,
                                                  'name',
                                                  staticEquipmentIndex,
                                                  documentIndex
                                                )
                                              }
                                              required
                                            />
                                          </TableCell>
                                          <TableCell>
                                            <TextField
                                              className={clsx({
                                                [classes.errorInputUnderline]:
                                                  !document.no
                                              })}
                                              id="document-no"
                                              name="no"
                                              multiline
                                              fullWidth
                                              value={document.no}
                                              onChange={(event) =>
                                                handleChangeDocumentDetail(
                                                  event.target.value,
                                                  'no',
                                                  staticEquipmentIndex,
                                                  documentIndex
                                                )
                                              }
                                              required
                                            />
                                          </TableCell>
                                          <TableCell
                                            className={classes.notClick}
                                          >
                                            <Typography color="textSecondary">
                                              {accessControl?.name ?? ''}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Autocomplete
                                              className={clsx({
                                                [classes.errorInputUnderline]:
                                                  !document.documentCreatorId
                                              })}
                                              id="document-creator"
                                              options={getRelatedUser(
                                                document.userIds,
                                                users
                                              )}
                                              getOptionLabel={(option) =>
                                                `${option.firstName} ${option.lastName}`
                                              }
                                              renderInput={(params) => (
                                                <TextField
                                                  {...params}
                                                  size="small"
                                                  required
                                                />
                                              )}
                                              value={getValue(
                                                document.documentCreatorId,
                                                users
                                              )}
                                              onChange={(event, newValue) =>
                                                handleChangeDocumentDetail(
                                                  newValue?.id ?? '',
                                                  'documentCreatorId',
                                                  staticEquipmentIndex,
                                                  documentIndex
                                                )
                                              }
                                            />
                                          </TableCell>
                                          <TableCell
                                            width={40}
                                            align="center"
                                            padding="none"
                                          >
                                            {selectedDocumentRefs.length ===
                                              0 && (
                                              <Tooltip title={t('bulkPlanning.tooltips.delete') as string} arrow>
                                                <IconButton
                                                  size="small"
                                                  onClick={() =>
                                                    handleOpenDeleteOneDialog(
                                                      document
                                                    )
                                                  }
                                                >
                                                  <DeleteRounded />
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                          </TableCell>
                                        </TableRow>
                                        <TableRow
                                          classes={{
                                            root: clsx(classes.unsetAll, {
                                              [selectedRow
                                                ? classes.tableRowRoot
                                                : classes.tableRowRootError]:
                                                selected
                                            }),
                                            selected: clsx({
                                              [selectedRow
                                                ? classes.tableRowSelected
                                                : classes.tableRowSelectedError]:
                                                selected
                                            })
                                          }}
                                          selected={selected}
                                        >
                                          {open && (
                                            <TableCell
                                              padding="none"
                                              colSpan={8}
                                            >
                                              <Table size="small">
                                                <TableHead>
                                                  <TableCell>{t('bulkPlanning.tableHeaders.purpose')}</TableCell>
                                                  <TableCell>
                                                    {t('bulkPlanning.tableHeaders.revision')}
                                                  </TableCell>
                                                  <TableCell
                                                    align="center"
                                                    padding="checkbox"
                                                  >
                                                    {t('bulkPlanning.tableHeaders.autoApproval')}
                                                  </TableCell>
                                                  <TableCell width={120}>
                                                    {t('bulkPlanning.tableHeaders.manHour')}
                                                  </TableCell>
                                                  <TableCell>
                                                    {t('bulkPlanning.tableHeaders.issueDate')}
                                                  </TableCell>
                                                  <TableCell>
                                                    {t('bulkPlanning.tableHeaders.completedDate')}
                                                    <HelpTooltip>
                                                      {t('bulkPlanning.mse.completedDateHelp')}
                                                    </HelpTooltip>
                                                  </TableCell>
                                                  <TableCell
                                                    width={40}
                                                    align="center"
                                                    padding="none"
                                                  >
                                                    {canUndo && (
                                                      <Tooltip
                                                        title={t('bulkPlanning.tooltips.undo') as string}
                                                        arrow
                                                      >
                                                        <IconButton
                                                          size="small"
                                                          onClick={() =>
                                                            onUndoDocumentRevision(
                                                              document
                                                            )
                                                          }
                                                        >
                                                          <UndoRounded />
                                                        </IconButton>
                                                      </Tooltip>
                                                    )}
                                                  </TableCell>
                                                </TableHead>
                                                <TableBody>
                                                  {document.projectDocumentRevisions.map(
                                                    (
                                                      documentRevision,
                                                      revisionIndex,
                                                      documentRevisions
                                                    ) => {
                                                      const purpose = getValue(
                                                        documentRevision.purposeId,
                                                        purposes
                                                      );
                                                      const revisionList =
                                                        purpose?.revisions ??
                                                        [];
                                                      const revision = getValue(
                                                        documentRevision.revisionId,
                                                        revisionList
                                                      );

                                                      return (
                                                        <TableRow
                                                          className={
                                                            classes.unsetAll
                                                          }
                                                          hover
                                                          key={revisionIndex}
                                                        >
                                                          <TableCell>
                                                            <Autocomplete
                                                              className={clsx(
                                                                classes.flexGrow2,
                                                                {
                                                                  [classes.errorInputUnderline]:
                                                                    !purpose
                                                                }
                                                              )}
                                                              id="purpose"
                                                              options={purposes}
                                                              getOptionLabel={(
                                                                option
                                                              ) =>
                                                                `${option.abbreviation}: ${option.fullName}`
                                                              }
                                                              renderInput={(
                                                                params
                                                              ) => (
                                                                <TextField
                                                                  {...params}
                                                                  size="small"
                                                                  required
                                                                />
                                                              )}
                                                              value={purpose}
                                                              onChange={(
                                                                event,
                                                                newValue
                                                              ) =>
                                                                handleChangeDocumentRevisionDetail(
                                                                  newValue?.id ??
                                                                    '',
                                                                  'purposeId',
                                                                  staticEquipmentIndex,
                                                                  documentIndex,
                                                                  revisionIndex
                                                                )
                                                              }
                                                            />
                                                          </TableCell>
                                                          <TableCell
                                                            width={128}
                                                          >
                                                            <Autocomplete
                                                              className={clsx({
                                                                [classes.errorInputUnderline]:
                                                                  !revision
                                                              })}
                                                              id="revision"
                                                              options={
                                                                revisionList
                                                              }
                                                              getOptionLabel={(
                                                                option
                                                              ) => option.name}
                                                              renderInput={(
                                                                params
                                                              ) => (
                                                                <TextField
                                                                  {...params}
                                                                  size="small"
                                                                  required
                                                                />
                                                              )}
                                                              value={revision}
                                                              onChange={(
                                                                event,
                                                                newValue
                                                              ) =>
                                                                handleChangeDocumentRevisionDetail(
                                                                  newValue?.id ??
                                                                    '',
                                                                  'revisionId',
                                                                  staticEquipmentIndex,
                                                                  documentIndex,
                                                                  revisionIndex
                                                                )
                                                              }
                                                            />
                                                          </TableCell>
                                                          <TableCell
                                                            className={clsx({
                                                              [classes.notClick]:
                                                                documentRevision.purposeId !==
                                                                PurposeLabel.IFR
                                                            })}
                                                            align="center"
                                                            padding="checkbox"
                                                          >
                                                            {documentRevision.purposeId ===
                                                              PurposeLabel.IFR && (
                                                              <Checkbox
                                                                checked={
                                                                  documentRevision.isAutoApproval
                                                                }
                                                                color="primary"
                                                                disabled={
                                                                  documentRevision.purposeId !==
                                                                  PurposeLabel.IFR
                                                                }
                                                                onChange={(
                                                                  event
                                                                ) =>
                                                                  handleChangeDocumentRevisionDetail(
                                                                    event.target
                                                                      .checked,
                                                                    'isAutoApproval',
                                                                    staticEquipmentIndex,
                                                                    documentIndex,
                                                                    revisionIndex
                                                                  )
                                                                }
                                                              />
                                                            )}
                                                          </TableCell>
                                                          <TableCell>
                                                            <TextField
                                                              className={clsx({
                                                                [classes.errorInputUnderline]:
                                                                  !documentRevision.manHour
                                                              })}
                                                              type="number"
                                                              size="small"
                                                              value={
                                                                documentRevision.manHour
                                                                  ? documentRevision.manHour
                                                                  : ''
                                                              }
                                                              onChange={(
                                                                event
                                                              ) =>
                                                                handleChangeDocumentRevisionDetail(
                                                                  event.target
                                                                    .value,
                                                                  'manHour',
                                                                  staticEquipmentIndex,
                                                                  documentIndex,
                                                                  revisionIndex,
                                                                  {
                                                                    positiveNumber:
                                                                      true
                                                                  }
                                                                )
                                                              }
                                                              required
                                                            />
                                                          </TableCell>
                                                          <TableCell
                                                            width={200}
                                                          >
                                                            <KeyboardDatePicker
                                                              className={clsx({
                                                                [classes.errorInputUnderline]:
                                                                  !documentRevision.planIssueDate
                                                              })}
                                                              autoOk
                                                              size="small"
                                                              variant="inline"
                                                              InputAdornmentProps={{
                                                                position: 'end'
                                                              }}
                                                              value={
                                                                documentRevision.planIssueDate
                                                              }
                                                              onChange={(
                                                                event
                                                              ) =>
                                                                handleChangeDocumentRevisionDetail(
                                                                  event
                                                                    ? moment(
                                                                        event
                                                                      )
                                                                        .endOf(
                                                                          'day'
                                                                        )
                                                                        .toDate()
                                                                    : null,
                                                                  'planIssueDate',
                                                                  staticEquipmentIndex,
                                                                  documentIndex,
                                                                  revisionIndex
                                                                )
                                                              }
                                                              KeyboardButtonProps={{
                                                                'aria-label':
                                                                  'change plan issue date'
                                                              }}
                                                              required
                                                            />
                                                          </TableCell>
                                                          <TableCell
                                                            width={200}
                                                          >
                                                            <KeyboardDatePicker
                                                              className={clsx({
                                                                [classes.errorInputUnderline]:
                                                                  !documentRevision.planIssueDate
                                                              })}
                                                              autoOk
                                                              size="small"
                                                              variant="inline"
                                                              InputAdornmentProps={{
                                                                position: 'end'
                                                              }}
                                                              minDate={
                                                                documentRevision.planIssueDate
                                                              }
                                                              minDateMessage={t('bulkPlanning.common.dateValidation')}
                                                              value={
                                                                documentRevision.planCompletedDate
                                                              }
                                                              onChange={(
                                                                event
                                                              ) =>
                                                                handleChangeDocumentRevisionDetail(
                                                                  event
                                                                    ? moment(
                                                                        event
                                                                      )
                                                                        .endOf(
                                                                          'day'
                                                                        )
                                                                        .toDate()
                                                                    : null,
                                                                  'planCompletedDate',
                                                                  staticEquipmentIndex,
                                                                  documentIndex,
                                                                  revisionIndex
                                                                )
                                                              }
                                                              KeyboardButtonProps={{
                                                                'aria-label':
                                                                  'change complete issue date'
                                                              }}
                                                              required
                                                            />
                                                          </TableCell>
                                                          <TableCell
                                                            width={40}
                                                            align="center"
                                                            padding="none"
                                                          >
                                                            {selectedDocumentRefs.length ===
                                                              0 && (
                                                              <Tooltip
                                                                title={
                                                                  documentRevisions.length <=
                                                                  1
                                                                    ? (t('bulkPlanning.common.cannotDelete') as string) : (t('bulkPlanning.tooltips.delete') as string)
                                                                }
                                                                arrow
                                                              >
                                                                <span>
                                                                  <IconButton
                                                                    size="small"
                                                                    disabled={
                                                                      documentRevisions.length <=
                                                                      1
                                                                    }
                                                                    onClick={() =>
                                                                      onDeleteDocumentRevision(
                                                                        staticEquipment.id,
                                                                        document,
                                                                        documentRevision
                                                                      )
                                                                    }
                                                                  >
                                                                    <DeleteRounded />
                                                                  </IconButton>
                                                                </span>
                                                              </Tooltip>
                                                            )}
                                                          </TableCell>
                                                        </TableRow>
                                                      );
                                                    }
                                                  )}
                                                </TableBody>
                                              </Table>
                                            </TableCell>
                                          )}
                                        </TableRow>
                                      </React.Fragment>
                                    );
                                  }
                                )
                              ) : (
                                <TableRow>
                                  <TableCell colSpan={8} align="center">
                                    <Typography
                                      component="div"
                                      variant="caption"
                                    >
                                      {t('bulkPlanning.mse.noDocumentFound')}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                          </React.Fragment>
                        );
                      }
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : templateWithDocument.length === 0 ? (
              <div className={classes.notFoundError}>
                <Typography gutterBottom>{t('bulkPlanning.mse.oops')}</Typography>
                <Typography component="div" variant="caption">
                  {t('bulkPlanning.common.oopsCannotDisplay')}
                </Typography>
                <Typography component="div" variant="caption" gutterBottom>
                  {t('bulkPlanning.common.checkMasterData')}
                </Typography>
              </div>
            ) : (
              <div className={classes.notFoundError}>
                <Typography gutterBottom>{t('bulkPlanning.mse.omg')}</Typography>
                <Typography component="div" variant="caption">
                  {t('bulkPlanning.common.omgDeletedAll')}
                </Typography>
                <Typography component="div" variant="caption" gutterBottom>
                  {t('bulkPlanning.mse.clickResetMessage')}
                </Typography>
                <Button
                  color="primary"
                  variant="outlined"
                  size="small"
                  onClick={onReset}
                >
                  {t('bulkPlanning.actions.reset')}
                </Button>
              </div>
            )}
          </Collapse>
        </Paper>
        <div className={classes.actionButton}>
          <Button
            variant="outlined"
            color="primary"
            size="large"
            startIcon={<KeyboardArrowLeftRounded />}
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            {t('projectCreate.back')}
          </Button>
          {activeStep < stepKeys.length - 1 ? (
            <Button
              variant="contained"
              color="primary"
              size="large"
              endIcon={<KeyboardArrowRightRounded />}
              disabled={nextButton(activeStep).disabled}
              onClick={nextButton(activeStep).action}
            >
              {t('projectCreate.next')}
            </Button>
          ) : (
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={nextButton(activeStep).disabled}
            >
              {t('bulkPlanning.actions.createDocuments')}
            </Button>
          )}
        </div>
      </form>
      <ConfirmDeleteBox
        open={openDeleteOneDialog}
        text={selectedDocument?.name ?? t('bulkPlanning.common.untitledDocument') as string}
        onDelete={onDeleteDocument}
        onClose={handleOpenDeleteOneDialog}
      />
      <ConfirmDeleteBox
        open={openDeleteAllDialog}
        text={t('bulkPlanning.listOfDocuments') as string}
        onDelete={onDeleteSelectedDocuments}
        onClose={handleOpenDeleteAllDialog}
      >
        <ul className={classes.ul}>
          {selectedDocumentRefs.map((ref) => {
            const document = bulkDocuments.find(
              (document) => document.ref === ref
            );
            return <li key={ref}>{document?.name ?? t('bulkPlanning.common.untitledDocument')}</li>;
          })}
        </ul>
      </ConfirmDeleteBox>
    </div>
  );
}

function UnfoldMoreRounded(props: SvgIconProps) {
  return (
    <SvgIcon {...props}>
      <path d="m12 5.83 2.46 2.46c.39.39 1.02.39 1.41 0 .39-.39.39-1.02 0-1.41L12.7 3.7a.9959.9959 0 0 0-1.41 0L8.12 6.88c-.39.39-.39 1.02 0 1.41.39.39 1.02.39 1.41 0L12 5.83zm0 12.34-2.46-2.46a.9959.9959 0 0 0-1.41 0c-.39.39-.39 1.02 0 1.41l3.17 3.18c.39.39 1.02.39 1.41 0l3.17-3.17c.39-.39.39-1.02 0-1.41a.9959.9959 0 0 0-1.41 0L12 18.17z" />
    </SvgIcon>
  );
}

function UnfoldLessRounded(props: SvgIconProps) {
  return (
    <SvgIcon {...props}>
      <path d="M8.12 19.3c.39.39 1.02.39 1.41 0L12 16.83l2.47 2.47c.39.39 1.02.39 1.41 0 .39-.39.39-1.02 0-1.41l-3.17-3.17a.9959.9959 0 0 0-1.41 0l-3.17 3.17c-.4.38-.4 1.02-.01 1.41zm7.76-14.6a.9959.9959 0 0 0-1.41 0L12 7.17 9.53 4.7a.9959.9959 0 0 0-1.41 0c-.39.39-.39 1.03 0 1.42l3.17 3.17c.39.39 1.02.39 1.41 0l3.17-3.17c.4-.39.4-1.03.01-1.42z" />
    </SvgIcon>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: { margin: theme.spacing(1.5, 0, 12, 0) },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: theme.spacing(2)
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: theme.spacing(4)
    },
    clickable: { cursor: 'pointer' },
    notClick: { cursor: 'not-allowed' },
    unsetAll: {
      '& > *': {
        borderBottom: 'unset'
      }
    },
    unset: { borderBottom: 'unset' },
    tableRowRoot: {
      '&$tableRowSelected, &$tableRowSelected:hover': {
        backgroundColor: fade(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        )
      }
    },
    tableRowSelected: {
      backgroundColor: fade(
        theme.palette.primary.main,
        theme.palette.action.selectedOpacity
      )
    },
    tableRowRootError: {
      '&$tableRowSelected, &$tableRowSelected:hover': {
        backgroundColor: fade(
          theme.palette.secondary.main,
          theme.palette.action.selectedOpacity
        )
      }
    },
    tableRowSelectedError: {
      backgroundColor: fade(
        theme.palette.secondary.main,
        theme.palette.action.selectedOpacity
      )
    },
    staticEquipmentRow: {
      color: theme.palette.background.paper,
      backgroundColor: theme.palette.text.primary
    },
    countBadge: {
      minWidth: 24,
      marginRight: 4,
      marginBottom: 6,
      padding: theme.spacing(0.25, 0.25, 0.25, 0.5)
    },
    errorInputUnderline: {
      '& .MuiInput-underline:before, & .MuiInput-underline:hover:not(.Mui-disabled):before, & .MuiInput-underline:after':
        {
          borderBottomColor: theme.palette.error.main,
          borderBottomWidth: 2
        }
    },
    creatorField: {
      padding: theme.spacing(0.75, 1, 0.75, 2),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    revisionContainer: {
      display: 'flex',
      gap: theme.spacing(1),
      flexWrap: 'wrap',
      marginBottom: theme.spacing(1)
    },
    flexSpace: {
      display: 'flex',
      gap: theme.spacing(1),
      alignItems: 'center'
    },
    truncatedChip: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '100%' // Adjust this to control the maximum width of the Chip
    },
    notFoundError: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      padding: theme.spacing(1, 2)
    },
    marginFormControlLabel: { margin: theme.spacing(0, 1.5, 0, 0) },
    ul: { paddingLeft: theme.spacing(2) },
    actionButton: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    flexGrow: { flexGrow: 1 },
    flexGrow2: { flexGrow: 2 },
    my2: { margin: theme.spacing(2, 0) },
    mb1: { marginBottom: theme.spacing(1) },
    px1: { padding: theme.spacing(0, 1) },
    px2: { padding: theme.spacing(0, 2) },
    pr2: { paddingRight: theme.spacing(2) }
  })
);
